import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { RxStompService } from '@stomp/ng2-stompjs';
import { UserResponse } from 'algorilla-shared-libs';
import { NotificationsService } from 'bowman-angular2-notifications';
import { BowmanCacheService } from 'bowman-cache';
import { HelperFunctions } from 'bowman-commons';
import { BowmanLogger, BowmanLoggerService } from 'bowman-logger';
import { Observable, Observer, Subscription, interval } from 'rxjs';
import { RateLimitStateService, RateLimitDisplayState } from 'src/app/services/rate-limit/rate-limit-state.service';
import { Common } from 'src/app/dashboard/common';
import { DashboardDataService } from 'src/app/dashboard/dashboard-data.service';
import { AggregationRequestDto } from 'src/app/dashboard/model/aggregation-request-dto';
import { DashboardDto } from 'src/app/dashboard/model/dashboard-dto';
import { AppInstanceType } from 'src/app/model/app-instance-type';
import { UserDateFormat } from 'src/app/model/user';
import { DashboardManagerService } from 'src/app/services/dashboard-manager.service';
import { ManagementService } from 'src/app/services/management.service';
import { ActiveSearch } from 'src/app/shared/active-search';
import { ActiveSearchDetails } from 'src/app/shared/active-search-details';
import { GuiPluginDto } from 'src/app/shared/gui-plugin.dto';
import { SearchRequestDto } from 'src/app/shared/search-request-dto';
import { SearchThreadPoolStatus } from 'src/app/shared/search-thread-pool-status';
import { ModalComponent } from 'src/lib/modal/modal.component';
import { DynamicModuleService } from 'src/services/dynamic-module.service';
import { CustomizationConfiguration } from 'src/services/model/customization-configuration';
import { SecurityService } from 'src/services/security.service';
import { MenuEntry } from './model/menu-entry';
import { ThemeSelectorComponent } from '../shared/theme-selector/theme-selector.component';


const CACHE_EXPIRE_MS = 300000;

@Component({
     selector: 'app-header',
     templateUrl: './header.component.html',
     styleUrls: ['./header.component.scss'],
     changeDetection: ChangeDetectionStrategy.OnPush

})
export class HeaderComponent implements OnInit, OnDestroy {
     private log: BowmanLogger = new BowmanLogger(this._loggerService, "DashboardManagement component");
     public debugMode: boolean = false;
     @ViewChild('userOptionsModal', { static: false }) userOptionsModal: ModalComponent;
     _timer: any;
     constructor(
          private _dashboardDataService: DashboardDataService,
          private _securityService: SecurityService,
          private _datePipe: DatePipe,
          private _loggerService: BowmanLoggerService,
          private _cdr: ChangeDetectorRef,
          private _websocketService: RxStompService,
          private _managementService: ManagementService,
          private _notificationService: NotificationsService,
          private _bowmanCacheService: BowmanCacheService,
          private _dynamicModulesService: DynamicModuleService,
          private _router: Router,
          private renderer: Renderer2,
          private _rateLimitStateService: RateLimitStateService
     ) {
          this._dashboardDataService.listWidgets().subscribe(widgetsMap => {
               this.widgetsMap = widgetsMap;
          });

     }

     public menuEntries: Array<MenuEntry>;
     public appInstanceTypes = AppInstanceType;
     @Input() public selectedSection: AppInstanceType;
     @Input() public user: UserResponse;
     @Input() public customization: CustomizationConfiguration;
     public busy: Subscription;
     public userSettings: any = {};
     public userDateFormats: Array<UserDateFormat> = [];
     public userTimezones: Array<String> = [];
     public searchPoolList: Array<SearchThreadPoolStatus> = [];
     public searchPoolPercentage: number = 0;
     public searchPoolStatusColor: string = '#6c757d';
     public searchPoolStatusColorLight: string;
     public clusterNodeCount = 0;
     public clusterTotalActive = 0;
     public clusterTotalThreads = 0;
     public clusterTotalQueue = 0;
     public clusterStatusLevel: 'unknown' | 'ok' | 'busy' | 'saturated' = 'unknown';
     public clusterStatusLabel = 'Loading';
     public clusterLastUpdated: number = null;
     public widgetsMap: Map<string, Map<string, string>> = new Map();
     get countObjectKeys() { return HelperFunctions.countObjectKeys }
     public get rateLimitEnabled(): boolean {
          return this.rateLimitState.enabled;
     }
     public get projectedTokens(): number {
          return this._rateLimitStateService.projectedEffectiveTokens();
     }
     public get tokenMaxBurst(): number {
          return this.rateLimitState.maxBurst;
     }
     public get tokenFillPercent(): number {
          return this._rateLimitStateService.fillPercent();
     }
     public get storedTokens(): number {
          return Math.round(this.rateLimitState.availableTokens || 0);
     }
     public get tokenRefillRate(): number {
          return Math.round(this.rateLimitState.tokensPerSecond || 0);
     }
     public get secondsToFullBucket(): number | null {
          if (this.tokenRefillRate <= 0 || this.tokenMaxBurst <= 0) {
               return null;
          }
          const missing = Math.max(0, this.tokenMaxBurst - this.projectedTokens);
          if (missing <= 0) {
               return 0;
          }
          return Math.ceil(missing / this.tokenRefillRate);
     }
     public get tokenFillBarType(): string {
          if (this.tokenFillPercent >= 50) {
               return 'success';
          }
          if (this.tokenFillPercent >= 20) {
               return 'warning';
          }
          return 'danger';
     }
     public get tokenStatusLevel(): 'ok' | 'busy' | 'low' {
          if (this.tokenFillPercent >= 50) {
               return 'ok';
          }
          if (this.tokenFillPercent >= 20) {
               return 'busy';
          }
          return 'low';
     }
     public get tokenStatusColor(): string {
          switch (this.tokenStatusLevel) {
               case 'ok':
                    return '#18bc9c';
               case 'busy':
                    return '#f4661f';
               default:
                    return '#e74c3c';
          }
     }
     public get clusterFillBarType(): string {
          switch (this.clusterStatusLevel) {
               case 'ok':
                    return 'success';
               case 'busy':
                    return 'warning';
               case 'saturated':
                    return 'danger';
               default:
                    return 'secondary';
          }
     }
     public get clusterStatusTooltip(): string {
          if (!this.searchPoolList || this.searchPoolList.length === 0) {
               return 'Cluster search pool: waiting for data';
          }
          const nodes = this.clusterNodeCount === 1 ? '1 node' : this.clusterNodeCount + ' nodes';
          return 'Cluster: ' + this.clusterStatusLabel
               + ' · ' + nodes
               + ' · ' + this.clusterTotalActive + '/' + this.clusterTotalThreads + ' threads active'
               + (this.clusterTotalQueue > 0 ? ' · ' + this.clusterTotalQueue + ' queued' : '');
     }
     public get tokenStatusTooltip(): string {
          if (!this.rateLimitEnabled) {
               return '';
          }
          let message = 'Query tokens: ' + this.projectedTokens + '/' + this.tokenMaxBurst + ' effective';
          message += ' · ' + this.tokenRefillRate + '/s refill';
          message += ' · ' + this.storedTokens + ' stored';
          if (this.secondsToFullBucket !== null && this.secondsToFullBucket > 0) {
               message += ' · ~' + this.secondsToFullBucket + 's to full bucket';
          }
          return message;
     }
     public activeSearches: Map<string, ActiveSearch<SearchRequestDto | AggregationRequestDto>> = new Map();
     public activeSearchesDetails: Map<string, ActiveSearchDetails<SearchRequestDto | AggregationRequestDto>> = new Map();
     public displayUserSettings: boolean = false;
     public rateLimitState: RateLimitDisplayState = {
          enabled: false,
          availableTokens: 0,
          lastRefillTimeMs: 0,
          maxBurst: 0,
          tokensPerSecond: 0
     };
     private _rateLimitTimer: Subscription;
     private _rateLimitStateSub: Subscription;
     private _searchPoolSub: Subscription;
     private initMenu(): void {
          try {
               this.menuEntries = [];
               let availableModules: GuiPluginDto[] = this._dynamicModulesService.getAvailableModules();
               if (this.customization != null && this.customization.sections && this.customization.sections != null && this.customization.sections.length > 0) {
                    this._router.config.map(route => {
                         let index: number = this.customization.sections.indexOf(route.path as string);
                         switch (route.path) {
                              case "discovery":
                                   this.menuEntries[index] = new MenuEntry('Discovery', AppInstanceType[AppInstanceType.discovery], 'search');
                                   break;

                              case "dashboard":
                                   this.menuEntries[index] = new MenuEntry('Dashboards', AppInstanceType[AppInstanceType.dashboard], 'view_quilt');
                                   break;
                              case "security":
                                   if (this.user.admin) {
                                        this.menuEntries[index] = new MenuEntry('Security', AppInstanceType[AppInstanceType.security], AppInstanceType[AppInstanceType.security]);
                                   }
                                   break;

                              case "datatypes":
                                   if (this.user.admin || this.user.persistence || this.user.modeler) {
                                        this.menuEntries[index] = new MenuEntry('Data management', AppInstanceType[AppInstanceType.datatypes], 'developer_board');
                                   }
                                   break;
                              case "ingestion_management":
                                   if (availableModules.filter(guiPlugin => guiPlugin.name == 'CoralineProxy').length > 0) {
                                        let module: GuiPluginDto = availableModules.filter(guiPlugin => guiPlugin.name == 'CoralineProxy')[0];
                                        if (Common.isCoralineProxyAdmin(this.user) || Common.isCoralineProxyAdmin(this.user) || this.user.admin) {
                                             this.menuEntries[index] = new MenuEntry(module.displayName, AppInstanceType[AppInstanceType.ingestion_management], window.location.origin + '/' + module.moduleIcon);
                                        }
                                   }
                         }
                    });

               } else {
                    this.menuEntries.push(new MenuEntry('Discovery', AppInstanceType[AppInstanceType.discovery], 'search'));
                    this.menuEntries.push(new MenuEntry('Dashboards', AppInstanceType[AppInstanceType.dashboard], 'view_quilt'));
                    if (this.user.admin || this.user.persistence || this.user.modeler) {
                         this.menuEntries.push(new MenuEntry('Data management', AppInstanceType[AppInstanceType.datatypes], 'developer_board'));
                    }
                    if (availableModules.filter(guiPlugin => guiPlugin.name.trim() == 'CoralineProxy').length > 0) {
                         let module: GuiPluginDto = availableModules.filter(guiPlugin => guiPlugin.name == 'CoralineProxy')[0];
                         if (Common.isCoralineProxyAdmin(this.user) || Common.isCoralineProxyAdmin(this.user) || this.user.admin) {
                              this.menuEntries.push(new MenuEntry(module.displayName, AppInstanceType[AppInstanceType.ingestion_management], window.location.origin + '/' + module.moduleIcon));
                         }
                    }
                    if (this.user.admin) {
                         this.menuEntries.push(new MenuEntry('Security', AppInstanceType[AppInstanceType.security], AppInstanceType[AppInstanceType.security]));
                    }
               }
          } catch (e) {
               this.log.error("Header error", e);
          }
     }

     public logout(): void {
          this._securityService.logout().subscribe(r => {
               setTimeout(() => { window.location.href = './' }, 100);
          })
     }

     private static listAllDashboards(dashboardService: DashboardManagerService, BowmanCacheService: BowmanCacheService): Observable<Array<DashboardDto>> {
          return Observable.create((observer: Observer<Array<DashboardDto>>) => {
               let dashboardList: Array<DashboardDto> = [];
               BowmanCacheService.callService(dashboardService, 'getAllDashboardsList', CACHE_EXPIRE_MS, null).subscribe(allList => {
                    if (allList.status == 'OK') {
                         (allList.result as Array<DashboardDto>).forEach(d => {
                              dashboardList.push(d);
                         });
                         observer.next(dashboardList);
                         observer.complete();
                    } else {
                         observer.error(allList.message);
                         observer.complete();
                    }
               }, err => {
                    observer.error(err);
                    observer.complete();
               });
          })
     }
     public static listDashboards(user: UserResponse, dashboardService: DashboardManagerService, BowmanCacheService: BowmanCacheService): Observable<Array<DashboardDto>> {
          return Observable.create((observer: Observer<Array<DashboardDto>>) => {
               let dashboardList: Array<DashboardDto> = [];
               if (user && user.name == 'admin') {
                    HeaderComponent.listAllDashboards(dashboardService, BowmanCacheService).subscribe(allList => {
                         allList.forEach(d => {
                              dashboardList.push(d);
                         });
                         observer.next(dashboardList);
                         observer.complete();
                    }, err => {
                         observer.error(err);
                         observer.complete();
                    })

               } else {
                    dashboardService.getUserDashboardsList().subscribe(userList => {
                         if (userList.status == 'OK') {
                              (userList.result as Array<DashboardDto>).forEach(d => {
                                   dashboardList.push(d);
                              });
                              dashboardService.getGroupsDashboardsList().subscribe(groupList => {
                                   if (groupList.status == 'OK') {
                                        (groupList.result as Array<DashboardDto>).forEach(d => {
                                             dashboardList.push(d);
                                        });
                                        let resultList: Array<DashboardDto> = HelperFunctions.uniqueBy(dashboardList, function (x) { return x.id; });
                                        observer.next(resultList);
                                        observer.complete();
                                   } else {
                                        observer.error(groupList.message);
                                        observer.complete();
                                   }
                              }, err => {
                                   observer.error(err);
                                   observer.complete();

                              });
                         } else {
                              observer.error(userList.message);
                              observer.complete();
                         }
                    }, err => {
                         observer.error(err);
                         observer.complete();
                    });
               }
          });
     }

     public saveUserSettings(): void {
          if (this.userSettings['dateFormat']) {
               delete this.userSettings['dateFormat'];
          }
          this.busy = this._securityService.updateUserOptions(this.userSettings).subscribe(res => {
               this.user.options = HeaderComponent.setDisplayTimezone(this.user.options);
               this._bowmanCacheService.set('user', this.user);
               this._notificationService.success("User settings updated.");
               this._cdr.markForCheck();
               this.displayUserSettings = false;
          }, err => {
               this.log.error("Cannot update user settings", err);
               this._notificationService.error("Cannot update user settings.", err);

          });
     }
     public openModal(): void {
          this.userSettings = this.user.options;
          this.displayUserSettings = true;
     }
     public static setDisplayTimezone(userOptions: any): any {
          const DEFAULT_DATE_FORMAT = 'yyyy-MM-dd - HH:mm:ss';
          const DEFAULT_TIMEZONE = 'Client default';

          if (!userOptions) {
               userOptions = {};
          }

          // Ensure defaults exist when user never saved settings
          if (!userOptions['selectedDateFormat'] || userOptions['selectedDateFormat'].toString().trim() === '') {
               userOptions['selectedDateFormat'] = DEFAULT_DATE_FORMAT;
          }
          if (!userOptions['timeZone'] || userOptions['timeZone'].toString().trim() === '') {
               userOptions['timeZone'] = DEFAULT_TIMEZONE;
          }

          if (userOptions['showTimezone'] == 'true' || userOptions['showTimezone'] == true) {
               userOptions['showTimezone'] = true;
          } else {
               userOptions['showTimezone'] = false;
          }

          if (userOptions['showTimezone']) {
               userOptions['dateFormat'] = userOptions['selectedDateFormat'] + ' zz';
          } else {
               userOptions['dateFormat'] = userOptions['selectedDateFormat'];
          }

          return userOptions;
     }
     ngOnInit() {
          this.userDateFormats = Common.initializeDateFormats(this._datePipe);
          this.userTimezones = Common.initializeTimeZones();

          this.initMenu();
          this._rateLimitStateSub = this._rateLimitStateService.state$.subscribe(state => {
               this.rateLimitState = state;
               this._cdr.markForCheck();
          });
          this._rateLimitStateService.refresh().subscribe();
          this._rateLimitTimer = interval(1000).subscribe(() => this._cdr.markForCheck());
          this.initSearchPoolStatus();
          this._cdr.detectChanges()
     }
     ngOnDestroy() {
          if (this._rateLimitTimer) {
               this._rateLimitTimer.unsubscribe();
          }
          if (this._rateLimitStateSub) {
               this._rateLimitStateSub.unsubscribe();
          }
          if (this._searchPoolSub) {
               this._searchPoolSub.unsubscribe();
          }
     }

     private initSearchPoolStatus(): void {
          if (this._searchPoolSub) {
               this._searchPoolSub.unsubscribe();
          }
          this._searchPoolSub = this._websocketService.watch('/topic/searchPoolClusterStatus').subscribe(message => {
               try {
                    const searchPoolStatus: Array<SearchThreadPoolStatus> = JSON.parse(message.body);
                    this.searchPoolList = searchPoolStatus || [];
                    this.updateClusterSummary();
                    this._cdr.markForCheck();
               } catch (error) {
                    this.log.error('Cannot parse search pool cluster status', error);
               }
          }, error => {
               this.log.error('Search pool cluster status subscription error', error);
          });
     }

     private updateClusterSummary(): void {
          if (!this.searchPoolList || this.searchPoolList.length === 0) {
               this.clusterNodeCount = 0;
               this.clusterTotalActive = 0;
               this.clusterTotalThreads = 0;
               this.clusterTotalQueue = 0;
               this.searchPoolPercentage = 0;
               this.clusterStatusLevel = 'unknown';
               this.clusterStatusLabel = 'No data';
               this.searchPoolStatusColor = '#6c757d';
               this.clusterLastUpdated = null;
               return;
          }

          let active = 0;
          let threads = 0;
          let queue = 0;
          let lastUpdated = 0;
          this.searchPoolList.forEach((node,idx) => {
               active += node.active || 0;
               threads += node.threads || 0;
               queue += node.queue || 0;
               if (node.timestamp > lastUpdated) {
                    lastUpdated = node.timestamp;
               }
          });

          this.clusterNodeCount = this.searchPoolList.length;
          this.clusterTotalActive = active;
          this.clusterTotalThreads = threads;
          this.clusterTotalQueue = queue;
          this.clusterLastUpdated = lastUpdated > 0 ? lastUpdated : null;
          this.searchPoolPercentage = threads > 0 ? Math.round((active / threads) * 100) : 0;
          
          const allThreadsBusy = threads > 0 && active >= threads;
          const highUtilization = threads > 0 && active >= threads * 0.8;
          if (allThreadsBusy && queue > 0) {
               this.clusterStatusLevel = 'saturated';
               this.clusterStatusLabel = 'Saturated';
               this.searchPoolStatusColor = '#e74c3c';
               this.searchPoolStatusColorLight = ThemeSelectorComponent.shadeColor('#e74c3c', -25);
          } else if (highUtilization || queue > 0) {
               this.clusterStatusLevel = 'busy';
               this.clusterStatusLabel = 'Busy';
               this.searchPoolStatusColor = '#f4661f';
               this.searchPoolStatusColorLight = ThemeSelectorComponent.shadeColor('#f4661f', -25);
          } else {
               this.clusterStatusLevel = 'ok';
               this.clusterStatusLabel = 'OK';
               this.searchPoolStatusColor = '#18bc9c';
               this.searchPoolStatusColorLight = ThemeSelectorComponent.shadeColor('#18bc9c', -25);
          }
     }

     public killActiveSearch(clientId: string) {
          let clientIds: Array<string> = [];
          clientIds.push(clientId);
          this._managementService.killActiveSearches(clientIds).subscribe(res => {
               if (res.status == 'OK') {
                    this.activeSearches.forEach((value, key) => {
                         if (key == clientId) {
                              this.activeSearches.delete(key);
                         }
                    });
               }
               this._notificationService.success("Active search killed", "Killed " + res.result + " tasks.");
          }, err => {
               this._notificationService.error("Cannot kill active search: ", err);
          })
     }

     public enableDebugMode() {
          this.debugMode = !this.debugMode;
          if (this.debugMode) {
               this.renderer.addClass(document.body, "debug-mode");
          } else {
               this.renderer.removeClass(document.body, "debug-mode");
          }

          this._cdr.detectChanges();
     }
}


